import { CORS } from 'root/utils/cors/cors';
import JSTwigLoader from 'root/helpers/twigLoader';
import LoadingIndicator from 'root/helpers/loadingIndicator';
import { AjaxContentException } from 'root/main/core/ajax/ajaxContentException';
import { default as ajax_content } from 'root/main/core/ajax/ajax_content';
import { GeoMonster } from 'root/utils/cookies/geoMonster';
import LoadMoreController from 'root/main/core/ajax/ajaxContentControllers/loadMoreController';
import AlertBannerController from 'root/main/core/ajax/ajaxContentControllers/alertBannerController';
import PopupController from 'root/main/core/ajax/ajaxContentControllers/popupController';
import getLocale from 'root/utils/locale/getLocale';

const ajaxControllers = {
    LoadMoreController,
    AlertBannerController,
    PopupController,
};

export default function AjaxContent() {
    return (function (CORS, JSTwigLoader) {
        const INI_EVENT = 'DOMContentLoaded';
        let arrayOfContent = [];
        let configs;

        // --- ------ CONTENT OBJECT DEFINITION---------
        let Content = function Content(configs, index, locale) {
            this.configs = configs;
            this.index = index;
            this.locale = locale;
            this.uri = getURI();
            this.twig = null;
            this.selector = '';
            this.pagesObj = {};
            this.isClicked = false;
        };

        Content.prototype = {
            load() {
                if (this.isAllowed()) {
                    this.loadContent();
                } else {
                    //destroy yourself
                    arrayOfContent[this.index] = null;
                }
            },

            isAllowed() {
                let pattern;
                let allow_pattern = this.configLookup('allow', 'pattern');
                if (allow_pattern === false || this.isGeo() === false) {
                    return false;
                }

                if (this.configLookup('allow', 'flags') === false) {
                    this.configs.allow.flags = '';
                }

                pattern = new RegExp(allow_pattern, this.configs.allow.flags);
                return this.uri.match(pattern) !== null;
            },

            isGeo() {
                let gm = new GeoMonster();
                let useGeo = this.configLookup('allow', 'useGeo');
                let geoCookie = gm.getCookie('geoConfig');
                if (geoCookie === null) return false;
                let cookieGeoDivision = geoCookie[useGeo.division];

                if (Array.isArray(useGeo.target)) {
                    return !!(
                        typeof useGeo.division !== 'undefined' &&
                        useGeo.target.indexOf(cookieGeoDivision) >= 0
                    );
                } else {
                    return !!(
                        typeof useGeo.division !== 'undefined' &&
                        cookieGeoDivision.includes(useGeo.target[this.locale])
                    );
                }
            },

            getContainerEl(el) {
                /*
                   @TODO this is not a very scalable selector, it is better to target the data attribute for example: "data-loader=ls-loader"; which is attached to the container.
                   changing the selector entails testing the loading indicator in all of its use cases (e.g. resources load more, resellers, form submit)
                   Please consider creating a cypress test to cover all the use cases.
                */
                return el.parentNode.previousElementSibling;
            },

            getContainerSelector(el) {
                return this.getContainerEl(el).dataset.ajax;
            },

            triggerEvent(obj, event = 'DOMContentLoaded') {
                if (obj[event] && document.readyState !== 'complete') {
                    if (Array.isArray(obj[event])) {
                        obj[event].forEach(el => {
                            const selector = this.getContainerSelector(el);
                            this.pagesObj[selector] = 0;
                            el.addEventListener(event, this.fetchTwigContent.bind(this));
                        });
                    } else {
                        obj[event].addEventListener(
                            event,
                            this.fetchTwigContent.bind(this)
                        );
                    }
                } else {
                    this.fetchTwigContent();
                }
            },

            loadContent() {
                const event = this.configLookup('trigger_event');
                const eventObj = {
                    DOMContentLoaded: document,
                    load: window,
                    click: Array.from(
                        document.querySelectorAll(
                            `[data-ajax=${this.configLookup('name')}]`
                        )
                    ),
                };
                this.triggerEvent(eventObj, event);
            },

            fetchTwigContent(e) {
                if (e && e.type === 'click') {
                    e.preventDefault();
                    LoadingIndicator({
                        form: null,
                        loaderParentEl: this.getContainerEl(e.target),
                    }).create();
                    this.isClicked = true;
                    this.selector = this.getContainerSelector(e.target);
                    this.pagesObj[this.selector] = this.pagesObj[this.selector] + 1;
                    this.pagesObj['current_selector'] = this.selector;
                    this.pagesObj['product'] = document.body.dataset.product;
                }
                if (
                    this.configLookup('filename') !== false &&
                    this.configLookup('theme') !== false
                ) {
                    const twigLoader = new JSTwigLoader(
                        this.configLookup('filename'),
                        this.configLookup('theme'),
                        false,
                        this.pagesObj
                    );
                    twigLoader
                        .onSuccess(this.contentLoaderHandler.bind(this))
                        .onError(function (error) {
                            LoadingIndicator()?.hide();
                            throw new AjaxContentException(
                                'could not load the twig file error: ' + error
                            );
                        })
                        .getFile();
                }
            },

            configLookup() {
                let verified_config;
                let args = arguments;
                let root = this.configs;
                for (let i = 0; i < args.length; i++) {
                    if (typeof root[args[i]] === 'undefined') {
                        root = ajax_content.default_configs[0];
                    }
                    verified_config = root[args[i]];
                    root = root[args[i]];
                }
                if (typeof verified_config === 'undefined') {
                    return false;
                }
                return verified_config;
            },

            contentLoaderHandler(data) {
                this.twig = data;
                if (this.isClicked) {
                    LoadingIndicator()?.hide();
                    this.isClicked = false;
                }

                this.passDataToControllers();
            },

            passDataToControllers() {
                let controller, success;
                try {
                    const controllerFromJson = this.configLookup('controller', 'object');
                    controller = new ajaxControllers[controllerFromJson](
                        this.configLookup('name')
                    );
                    controller.setTwig(this.twig);
                    controller.setData(this.configLookup('controller', 'data'));
                    controller.setSelector(this.selector);
                    controller.load();
                    success = true;
                } catch (e) {
                    success = false;
                }
                return {
                    success: success,
                    controller: controller,
                };
            },
        };

        // --- ------ PRIVATE METHODS && OBJECTS---------
        let getURI = function getCurrentPage() {
            return window.location.pathname;
        };

        let loadConfigs = function loadConfigs() {
            if (ajax_content !== undefined) {
                let locale = getLocale();
                configs = localizeConfigs(ajax_content, locale);
                configLoadedHandler();
            } else {
                throw new AjaxContentException('Could not load config file');
            }
        };

        let localizeConfigs = function localizeConfigs(data, locale) {
            if (!data[locale] && !data['*']) return [];
            if (!data[locale]) data[locale] = [];
            if (!data['*']) data['*'] = [];
            if (typeof data[locale] === 'string')
                return localizeConfigs(data, data[locale]); // in case of "hq" : "au", (where "hq" : {...})
            return data['*'].concat(data[locale]);
        };

        let init = function init() {
            loadConfigs();
        };

        let configLoadedHandler = function configLoadedHandler() {
            if (configs instanceof Array && configs.length > 0) {
                configs.forEach((content, index) => {
                    let c = new Content(content, index, getLocale());
                    c.load();
                });
            }
        };
        document.addEventListener(INI_EVENT, init);
    })(CORS, JSTwigLoader);
}
